import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {
  DeviceConnectionContext,
  SET_DEVICE_CONNECTION_DATA,
  SET_SELECTED_DEVICE_CONNECTION_DATA,
  SET_SHOW_HIDE_DEVICE_CONNECTION_POPUP,
} from './context/context'
import InputText from '../../common/InputText'
import DeviceConnectionTable from './DeviceConnectionTable'
import DeviceConnectionFormModal from './form'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Button, Flex, Form, Modal, Row} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const DeviceConnectionList = () => {
  const intl = useIntl()
  let {vehicle} = useParams()

  const [state, dispatch] = useContext(DeviceConnectionContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/device_connection/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const getDeviceConnectionsData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'deviceConnections.fetchingData',
        }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({type: SET_DEVICE_CONNECTION_DATA, data: res})

    navDispatch({
      type: 'loader',
      data: {showLoader: false},
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'deviceConnections.loadError' }))
    }
  }

  const buildUrl = () => {
    const params = new URLSearchParams({vehicle})
    if (search) params.append('search', search)
    return `/api/device_connection/?${params.toString()}`
  }

  useEffect(() => {
    currentUrl.current = buildUrl()
    getDeviceConnectionsData()
  }, [debouncedSearch])

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({id: 'layout.adminTools'}),
        sub_header:
          intl.formatMessage({id: 'layout.vehicleList'}) +
          ` / ${vehicle} / ` +
          intl.formatMessage({id: 'sidebar.deviceConnections'}),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
  }, [])

  const onEdit = (record) => {
    dispatch({
      type: SET_SELECTED_DEVICE_CONNECTION_DATA,
      data: {
        ...record,
        index: state.data?.results?.indexOf(record),
      },
    })
  }

  const deleteDeviceConnection = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'deviceConnections.deleting',
        }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await deleteRequest(
      `/api/device_connection/${record.id}/?vehicle=${vehicle}`
    )
    navDispatch({
      type: 'loader',
      data: {showLoader: false},
      notification: {},
    })
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: 'deviceConnections.deleteSuccess',
          }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getDeviceConnectionsData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({id: 'deviceConnections.deleteError'}),
        },
      })
    }
  }

  const onCreateDeviceConnection = () => {
    dispatch({
      type: SET_SHOW_HIDE_DEVICE_CONNECTION_POPUP,
      data: true,
    })
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({
        id: 'deviceConnections.confirmDelete',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'deviceConnections.confirmDeleteContent',
      }),
      onOk: () => deleteDeviceConnection(record),
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.data?.results?.length) return []
    return state.data.results.map((item, index) => ({
      ...item,
      key: `dc-${index}`,
      rowKey: `dc-${item.id}`,
      index,
    }))
  }, [state.data])

  return (
    <>
      {contextHolder}

      <DeviceConnectionFormModal
        getDeviceConnectionsData={getDeviceConnectionsData}
      />

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex gap={16} justify="space-between" className="full-width">
              <Flex gap={16} align="center" className='full-width'>
                <InputText
                  name="search_value"
                  type="search"
                  placeholder={intl.formatMessage({
                    id: 'deviceConnections.searchPlaceholder',
                  })}
                  value={search}
                  isRequired={false}
                  wrapperClass="search-device-connection"
                  onChange={(value) => setSearch(value)}
                />
              </Flex>
              <Button onClick={onCreateDeviceConnection}>
                {intl.formatMessage({
                  id: 'deviceConnections.createNew',
                })}
              </Button>
            </Flex>
          </Form>

          <Flex vertical gap={16} style={{width: '100%'}}>
            <DeviceConnectionTable
              dataSource={dataSource}
              pagination={pagination}
              getDeviceConnectionsData={getDeviceConnectionsData}
              onEdit={onEdit}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default DeviceConnectionList
